/**
 * Build step 9 — Capture (voice and text) with the held list, and Ideas. ADR-001 §8.4, §10.2, §11.11, amended by
 * ADR-002 Part II (in-browser transcription) and Part III (t-cd196043: server transcription retired — no audio is
 * uploaded, played back or transcribed by the server; the R1 setting is gone).
 *
 * Who: `capture.use` (today founder, ops AD, ops member — never sales, marketing or HR). Visibility: a note, its
 * items, held items and the auto-created log are the author's; managers (`tasks.read.all`) see everyone's.
 * Anyone else gets 404, never 403. Editing a note in progress is the author's alone (a manager who can see it: 403).
 * Every write is audited by the database trigger with the real actor and the acting-as user (ADR-006).
 *
 * The capture link (`?t=`) reaches exactly two routes: GET /capture/link (a greeting and the recogniser's language)
 * and POST /capture/notes — create-only, for the token's owner, never a read of anyone's data. ADR-001 §11.1 named
 * only POST /capture/notes; the greeting is what ADR-002 Part II needs on a phone.
 */
import { z } from 'zod';
import { IdempotencyHeaders, IfMatchHeaders, OptionalIfMatchHeaders } from './common.js';
import { AutoCreatedPage, AutoCreatedTaskParams, CaptureItemParams, CaptureLinkInfo, CaptureNote, CaptureNoteAccepted, CaptureNoteIdParams, CaptureNotePage, CaptureTokenQuery, ConfirmCaptureBody, ConfirmCaptureResponse, ConfirmHeldBody, CreateCaptureNoteBody, DiscardHeldBody, HeldBacklog, HeldIdParams, HeldItem, HeldPage, ListAutoCreatedQuery, ListCaptureNotesQuery, ListHeldQuery, MyCaptureSettings, UndoAutoCreatedBody, UndoCaptureNoteResponse, UpdateCaptureItemBody, UpdateCaptureItemResponse, } from './capture.js';
import { CreateIdeaBody, DecideIdeaBody, Idea, IdeaIdParams, IdeaLogBody, IdeaPage, ListIdeasQuery, ReplyIdeaBody } from './ideas.js';
import { API_BASE, defineRoute } from './route.js';
import { DropResponse, Task } from './tasks.js';
const CAPTURE = ['capture.use'];
const IDEAS = ['ideas.submit', 'ideas.decide'];
const OWN = 'The author; managers see everyone’s. Anyone else: 404.';
const AUTHOR = 'The author only. A manager who can see the note: 403. Anyone else: 404.';
/** Optional `?t=` on the token-capable session route. */
const OptionalToken = z.object({ t: CaptureTokenQuery.shape.t.optional() });
const ACT_HEADERS = IdempotencyHeaders.extend(OptionalIfMatchHeaders.shape);
const ACTION = { idempotency: 'key', concurrency: 'if-match-optional', rateLimit: 'write' };
export const step9Routes = {
    // ============================================================ capture link
    'capture.linkInfo': defineRoute({
        id: 'capture.linkInfo',
        method: 'GET',
        path: `${API_BASE}/capture/link`,
        summary: 'The capture link page: a greeting and the recogniser’s language (no app, no login each time)',
        description: 'Reads nothing but the owner’s first name (and the shared `stt.note_language`). The phone transcribes the note ' +
            'itself; no audio is ever uploaded. Sent with Referrer-Policy: no-referrer and Cache-Control: no-store; the ' +
            'token never appears in a log line.',
        access: { auth: 'capture_token', note: 'A valid capture token (30 calls an hour per token). Unknown, rotated or disabled: 404.' },
        query: CaptureTokenQuery,
        success: { status: 200, description: 'Who the link belongs to, as a greeting', schema: CaptureLinkInfo },
        errors: ['not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ capture notes
    'capture.createNote': defineRoute({
        id: 'capture.createNote',
        method: 'POST',
        path: `${API_BASE}/capture/notes`,
        summary: 'Capture a note — typed, or transcribed in the browser and checked before saving',
        description: 'Text only: a recording never leaves the browser. A voice note dictated live through the browser\'s speech ' +
            'service sends text_origin browser + transcript_source live (+ audio_seconds, at most 120) (ADR-002 §24.5); ' +
            'typed text sends text_origin typed. browser_stt (the retired in-browser Whisper) is an unknown field → 422; notes it ' +
            'made earlier still read back with their tier. Returns at once; the language model runs in the worker ' +
            '(never on this request). `proposing`, then `proposed` with `settle_at` = now + the countdown (a pg-boss job, ' +
            'so it survives a closed browser). If the model fails after its retries, the note becomes one held item with ' +
            'the raw text — never lost. Via the link (source=link) the response is all the caller can ever read; the ' +
            'items settle on the timer and the author sees them in the app.',
        access: {
            auth: 'session',
            anyOf: CAPTURE,
            captureToken: true,
            note: 'Founder, ops AD, ops member; or a capture token (for its owner, 30 notes an hour). Others: 403.',
        },
        query: OptionalToken,
        headers: IdempotencyHeaders,
        body: CreateCaptureNoteBody,
        success: { status: 202, description: 'Accepted', schema: CaptureNoteAccepted },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'capture.listNotes': defineRoute({
        id: 'capture.listNotes',
        method: 'GET',
        path: `${API_BASE}/capture/notes`,
        summary: 'My captured notes, newest first',
        access: { auth: 'session', anyOf: CAPTURE, note: 'Own notes; managers may pass by_id. Others: 403.' },
        query: ListCaptureNotesQuery,
        success: { status: 200, description: 'A page of notes', schema: CaptureNotePage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'capture.getNote': defineRoute({
        id: 'capture.getNote',
        method: 'GET',
        path: `${API_BASE}/capture/notes/{id}`,
        summary: 'A note: its transcript, the proposed items with confidence, and the countdown',
        description: 'Poll while `proposing` (the SPA polls every 2 s while open). Items: tasks, possible ' +
            'escalations (never raised unless ticked) and statements for client notes. `will` says what the countdown ' +
            'does to each task; `confidence_explain` is the (i) text.',
        access: { auth: 'session', anyOf: CAPTURE, note: OWN },
        params: CaptureNoteIdParams,
        success: { status: 200, description: 'The note', schema: CaptureNote, headers: { ETag: 'The note version' } },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'capture.updateItem': defineRoute({
        id: 'capture.updateItem',
        method: 'PATCH',
        path: `${API_BASE}/capture/notes/{id}/items/{iid}`,
        summary: 'Edit a proposed item: title, client, person, date, kind, priority, severity, or tick to raise',
        description: 'Only while `proposed` or `editing` (409 once settled). Confidence is recomputed with scoreCapture: a client ' +
            'picked counts as recognised, a date as heard, and a person picked adds +10 (capped at 97). If-Match is the ' +
            'NOTE’s version, so an edit can never cross the countdown settling (412 with the note as it is now). ' +
            'The person must be an active founder, ops AD or ops member; the client must be one the author can see (404). ' +
            '`raise` only on escalations; `assignee_id`, `due`, `priority` only on tasks (422 otherwise).',
        access: { auth: 'session', anyOf: CAPTURE, note: AUTHOR },
        params: CaptureItemParams,
        headers: IfMatchHeaders,
        body: UpdateCaptureItemBody,
        success: { status: 200, description: 'The item, rescored', schema: UpdateCaptureItemResponse },
        preconditionCurrent: CaptureNote,
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'capture.pause': defineRoute({
        id: 'capture.pause',
        method: 'POST',
        path: `${API_BASE}/capture/notes/{id}/pause`,
        summary: '"Edit": pause the countdown while you edit',
        description: 'From `proposed` → `editing`; the settle job is cancelled and the seconds left are kept. The pause ends on its ' +
            'own after 10 minutes (CAPTURE_EDIT_PAUSE_MAX_SECONDS), so a closed tab cannot strand items. Pausing an ' +
            '`editing` note returns it unchanged. 409 otherwise.',
        access: { auth: 'session', anyOf: CAPTURE, note: AUTHOR },
        params: CaptureNoteIdParams,
        success: { status: 200, description: 'The note, paused', schema: CaptureNote },
        errors: ['no_session', 'not_found', 'forbidden', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'capture.resume': defineRoute({
        id: 'capture.resume',
        method: 'POST',
        path: `${API_BASE}/capture/notes/{id}/resume`,
        summary: '"Resume timer": carry on from the seconds left',
        description: 'From `editing` → `proposed`, settle_at = now + the seconds left. Resuming a `proposed` note returns it unchanged. 409 otherwise.',
        access: { auth: 'session', anyOf: CAPTURE, note: AUTHOR },
        params: CaptureNoteIdParams,
        success: { status: 200, description: 'The note, counting down', schema: CaptureNote },
        errors: ['no_session', 'not_found', 'forbidden', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'capture.confirm': defineRoute({
        id: 'capture.confirm',
        method: 'POST',
        path: `${API_BASE}/capture/notes/{id}/confirm`,
        summary: '"Confirm now": every ticked item becomes real; escalations only when ticked',
        description: 'One transaction: the listed task items become tasks (source voice, origin the note) whatever their ' +
            'confidence; task items not listed are held; ticked escalations are raised (owner: the client’s SPOC), the ' +
            'rest are not; statements with a client go to client notes (kind voice_statement), others stay in the note. ' +
            'Only from `proposed` or `editing` — 409 once the countdown has settled it (read the note to see what happened). ' +
            'Ids must be this note’s task / escalation items (422).',
        access: { auth: 'session', anyOf: CAPTURE, note: AUTHOR },
        params: CaptureNoteIdParams,
        headers: IdempotencyHeaders,
        body: ConfirmCaptureBody,
        success: { status: 200, description: 'What was created', schema: ConfirmCaptureResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'capture.cancel': defineRoute({
        id: 'capture.cancel',
        method: 'POST',
        path: `${API_BASE}/capture/notes/{id}/cancel`,
        summary: '"Cancel": nothing is created',
        description: 'From any state before `done`. Nothing becomes a task, an escalation, a held item or a client note; the note ' +
            'and its transcript are kept (audited). Cancelling a cancelled note is 204 again; a `done` note is 409.',
        access: { auth: 'session', anyOf: CAPTURE, note: AUTHOR },
        params: CaptureNoteIdParams,
        success: { status: 204, description: 'Cancelled. Nothing was added.' },
        errors: ['no_session', 'not_found', 'forbidden', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'capture.undoNote': defineRoute({
        id: 'capture.undoNote',
        method: 'POST',
        path: `${API_BASE}/capture/notes/{id}/undo`,
        summary: '"Undo all": remove the tasks and held items this note just made',
        description: 'The author, within 15 minutes of the note finishing (`undo_all_until`) and while every task it made is still ' +
            'open and unchanged — otherwise 409 "Undo each task from the log, with a reason". Raised escalations and ' +
            'client-note statements stay. Removed rows are audited, not turned into dropped items (the author is ' +
            'reversing their own capture moments after making it).',
        access: { auth: 'session', anyOf: CAPTURE, note: AUTHOR },
        params: CaptureNoteIdParams,
        success: { status: 200, description: 'What was removed', schema: UndoCaptureNoteResponse },
        errors: ['no_session', 'not_found', 'forbidden', 'conflict'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    // ============================================================ held list
    'held.list': defineRoute({
        id: 'held.list',
        method: 'GET',
        path: `${API_BASE}/held-items`,
        summary: 'Held for review: items the system was not sure about, oldest first',
        description: 'Also shown in Team → Review queue (kind `held`) and on the managers’ Today. Default status open.',
        access: { auth: 'session', anyOf: CAPTURE, note: 'Managers: all. Others with capture: what they captured. Others: 403.' },
        query: ListHeldQuery,
        success: { status: 200, description: 'A page of held items', schema: HeldPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'held.backlog': defineRoute({
        id: 'held.backlog',
        method: 'GET',
        path: `${API_BASE}/held-items/backlog`,
        summary: 'The held-item backlog: open, late, held rate, split by how the text was transcribed',
        description: 'The live quality signal for transcription (ADR-002 §17.2–17.3), on the dashboard from day one of step 9. A ' +
            'held rate that rises for one browser tier points at transcription; one that rises everywhere, at resolution.',
        access: { auth: 'session', anyOf: ['tasks.read.all'], note: 'Managers. Others: 403.' },
        success: { status: 200, description: 'The backlog now', schema: HeldBacklog },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'held.confirm': defineRoute({
        id: 'held.confirm',
        method: 'POST',
        path: `${API_BASE}/held-items/{id}/confirm`,
        summary: 'Review queue "Confirm": make it a task',
        description: 'Defaults: title the held text, due in 2 days, priority medium, source voice; origin_ref the held item. The ' +
            'person must be an active founder, ops AD or ops member; the client one the caller can see (404). 409 when ' +
            'not open (someone else resolved it).',
        access: { auth: 'session', anyOf: CAPTURE, note: 'Managers, or the author. Anyone else: 404.' },
        params: HeldIdParams,
        headers: ACT_HEADERS,
        body: ConfirmHeldBody,
        success: { status: 201, description: 'The task', schema: Task },
        preconditionCurrent: HeldItem,
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'held.discard': defineRoute({
        id: 'held.discard',
        method: 'POST',
        path: `${API_BASE}/held-items/{id}/discard`,
        summary: 'Review queue "Discard": leave it out, with a reason',
        description: 'Writes a dropped item (source voice) — Team → Dropped tasks. 409 when not open.',
        access: { auth: 'session', anyOf: CAPTURE, note: 'Managers, or the author. Anyone else: 404.' },
        params: HeldIdParams,
        headers: ACT_HEADERS,
        body: DiscardHeldBody,
        success: { status: 200, description: 'The dropped item', schema: DropResponse },
        preconditionCurrent: HeldItem,
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    // ============================================================ auto-created log
    'autoLog.list': defineRoute({
        id: 'autoLog.list',
        method: 'GET',
        path: `${API_BASE}/capture/auto-log`,
        summary: 'Auto-approved log: what the countdown created, from which note, newest first',
        description: 'Team → Review queue "Auto-approved log" (managers, everyone) and Capture "Your recent captures" (own).',
        access: { auth: 'session', anyOf: CAPTURE, note: 'Managers: all; may pass by_id. Others with capture: their own. Others: 403.' },
        query: ListAutoCreatedQuery,
        success: { status: 200, description: 'A page of auto-created tasks', schema: AutoCreatedPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'autoLog.undo': defineRoute({
        id: 'autoLog.undo',
        method: 'POST',
        path: `${API_BASE}/capture/auto-log/{task_id}/undo`,
        summary: '"Undo" an auto-created task, with a reason',
        description: 'Removes that task by id (never by title match) and writes a dropped item (source voice). 409 when the task ' +
            'is closed, dropped or already undone; 404 when it was not auto-created from a capture you can see.',
        access: { auth: 'session', anyOf: CAPTURE, note: 'Managers, or the author. Anyone else: 404.' },
        params: AutoCreatedTaskParams,
        headers: ACT_HEADERS,
        body: UndoAutoCreatedBody,
        success: { status: 200, description: 'The dropped item', schema: DropResponse },
        preconditionCurrent: Task,
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    // ============================================================ my capture settings
    'me.captureSettings': defineRoute({
        id: 'me.captureSettings',
        method: 'GET',
        path: `${API_BASE}/me/capture`,
        summary: 'My capture settings: the recogniser’s language, the threshold and countdown, my link',
        access: { auth: 'session', anyOf: CAPTURE, note: 'Founder, ops AD, ops member. Others: 403.' },
        success: { status: 200, description: 'My settings', schema: MyCaptureSettings },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ ideas
    'ideas.list': defineRoute({
        id: 'ideas.list',
        method: 'GET',
        path: `${API_BASE}/ideas`,
        summary: 'Ideas, newest first, with the four tiles and the stage pill counts',
        description: 'Deciders see every idea; others see their own plus approved, building and live ones.',
        access: { auth: 'session', anyOf: IDEAS, note: 'Everyone who may suggest (not HR). Others: 403.' },
        query: ListIdeasQuery,
        success: { status: 200, description: 'A page of ideas', schema: IdeaPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'ideas.create': defineRoute({
        id: 'ideas.create',
        method: 'POST',
        path: `${API_BASE}/ideas`,
        summary: '"Suggest an idea" — sent for a decision',
        access: { auth: 'session', anyOf: IDEAS, note: 'Everyone who may suggest (not HR). Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateIdeaBody,
        success: { status: 201, description: 'Sent. The Ops associate director will decide.', schema: Idea },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'ideas.get': defineRoute({
        id: 'ideas.get',
        method: 'GET',
        path: `${API_BASE}/ideas/{id}`,
        summary: 'One idea with its trail and proof',
        access: { auth: 'session', anyOf: IDEAS, note: 'Deciders; the author; anyone once approved, building or live. Else 404.' },
        params: IdeaIdParams,
        success: { status: 200, description: 'The idea', schema: Idea },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'ideas.decide': defineRoute({
        id: 'ideas.decide',
        method: 'POST',
        path: `${API_BASE}/ideas/{id}/decide`,
        summary: 'Approve (who builds it, target date), ask for detail, or decline (with a note)',
        description: 'Only from new or needs_info (409). The owner must be an active founder, ops AD or ops member.',
        access: {
            auth: 'session',
            anyOf: IDEAS,
            action: ['ideas.decide'],
            note: 'Deciders (founder, ops AD). The author or others who can see it: 403. Else 404.',
        },
        params: IdeaIdParams,
        headers: ACT_HEADERS,
        body: DecideIdeaBody,
        success: { status: 200, description: 'The idea', schema: Idea },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'ideas.reply': defineRoute({
        id: 'ideas.reply',
        method: 'POST',
        path: `${API_BASE}/ideas/{id}/reply`,
        summary: '"Add the detail asked for" — back to waiting for a decision',
        description: 'Only from needs_info (409). The answer is appended to "What would we do?" and to the trail.',
        access: { auth: 'session', anyOf: IDEAS, note: 'The author. Deciders: 403. Else 404.' },
        params: IdeaIdParams,
        headers: ACT_HEADERS,
        body: ReplyIdeaBody,
        success: { status: 200, description: 'The idea', schema: Idea },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
    'ideas.log': defineRoute({
        id: 'ideas.log',
        method: 'POST',
        path: `${API_BASE}/ideas/{id}/log`,
        summary: 'Progress: start building, mark live with proof, or accept the result',
        description: '`building` from approved (deciders). `live` from building (deciders or the owner) — proof and a result line ' +
            'required. `result_checked` on a live idea (deciders, never the person who marked it live: 403). 409 when the ' +
            'idea is not at that step.',
        access: { auth: 'session', anyOf: IDEAS, note: 'As per step (see description). Visible but not allowed: 403. Else 404.' },
        params: IdeaIdParams,
        headers: ACT_HEADERS,
        body: IdeaLogBody,
        success: { status: 200, description: 'The idea', schema: Idea },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict', 'precondition_failed'],
        ...ACTION,
    }),
};
//# sourceMappingURL=routes.step9.js.map